import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  Text,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'

export type SubmitConfirmSummary = {
  /** 計画タイプ表示名 (= 需要調達計画。BFF は demand-procurement 固定のため常に同じ値)。 */
  planTypeLabel: string
  /** 対象期間表示 (= 断面 + 対象日、呼び出し側で整形済み)。 */
  periodLabel: string
  /** エリア表示名。 */
  areaLabel: string
  /** 調達合計 (48 コマの調達計・合算、kWh)。 */
  procurementTotalKwh: number
  /** 販売合計 (48 コマの販売計・合算、kWh)。 */
  salesTotalKwh: number
  /** 需給バランス (調達合計 − 需要計画値合計 − 販売合計、spec AC-006、kWh)。 */
  balanceKwh: number
  /** 前回提出からの変更コマ数。 */
  changedSlotCount: number
  /** 対象期間の需給バランスにマイナスのコマが 1 つでもあるか (= 警告表示条件)。 */
  hasNegativeBalance: boolean
}

type Props = {
  isOpen: boolean
  isSubmitting: boolean
  summary: SubmitConfirmSummary
  onClose: () => void
  onConfirm: () => void
}

const rowLabelStyle: React.CSSProperties = {
  fontSize: fontSizes.sm.value,
  color: LightModeSemanticColors.Text.midEmphasis.value,
}

const rowValueStyle: React.CSSProperties = {
  fontSize: fontSizes.md.value,
  fontWeight: 600,
  color: LightModeSemanticColors.Text.highEmphasis.value,
}

type SummaryRowProps = {
  label: string
  value: string
}

const SummaryRow = ({ label, value }: SummaryRowProps): React.ReactElement => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <span style={rowLabelStyle}>{label}</span>
    <span style={rowValueStyle}>{value}</span>
  </div>
)

/**
 * 需要調達計画 提出確認ダイアログ (AC-01, spec AC-PLC-029)。
 *
 * サマリー (計画タイプ・対象期間・エリア・調達合計・販売合計・需給バランス・
 * 前回提出からの変更コマ数) は呼び出し側 (index.tsx) が既取得の
 * GetProcurementPlanDetail データから client 集計して渡す。 [rule 逸脱]
 * 確認画面だがサーバー計算値を表示しない。 提出 payload は plan_id のみで数値を送信しないため、 サマリーの数値は
 * 送信内容を構成せず detail 画面が既に表示している値の再掲にすぎない
 * (提出可否の業務判定は backend gate + worker build が担う)。
 *
 * 需給バランスがマイナスのコマがある場合は警告文言を表示するが、 提出は
 * ブロックしない (AC-01)。
 *
 * `~/components/common/Modal` を wrap する (generation-sales の
 * SubmitConfirmDialog と同じパターン)。
 */
export const SubmitConfirmDialog = ({
  isOpen,
  isSubmitting,
  summary,
  onClose,
  onConfirm,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const footer = (
    <SaveCancelFooter
      isPending={isSubmitting}
      onCancel={onClose}
      onSave={onConfirm}
      saveLabel={t('page:procurementPlanDetail.submitConfirm.confirm')}
      saveLoadingText={t('page:procurementPlanDetail.submitConfirm.submitting')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('page:procurementPlanDetail.submitConfirm.title')}
    >
      <VStack align="stretch" gap={3}>
        <Text>{t('page:procurementPlanDetail.submitConfirm.body')}</Text>
        <VStack align="stretch" gap={2}>
          <SummaryRow
            label={t('page:procurementPlanDetail.submitConfirm.planType')}
            value={summary.planTypeLabel}
          />
          <SummaryRow
            label={t('page:procurementPlanDetail.submitConfirm.period')}
            value={summary.periodLabel}
          />
          <SummaryRow
            label={t('page:procurementPlanDetail.submitConfirm.area')}
            value={summary.areaLabel}
          />
          <SummaryRow
            label={t(
              'page:procurementPlanDetail.submitConfirm.procurementTotal',
            )}
            value={`${summary.procurementTotalKwh.toLocaleString()} kWh`}
          />
          <SummaryRow
            label={t('page:procurementPlanDetail.submitConfirm.salesTotal')}
            value={`${summary.salesTotalKwh.toLocaleString()} kWh`}
          />
          <SummaryRow
            label={t('page:procurementPlanDetail.submitConfirm.balance')}
            value={`${summary.balanceKwh.toLocaleString()} kWh`}
          />
          <SummaryRow
            label={t(
              'page:procurementPlanDetail.submitConfirm.changedSlotCount',
            )}
            value={`${summary.changedSlotCount}${t('page:procurementPlanDetail.submitConfirm.changedSlotCountUnit')}`}
          />
        </VStack>
        {summary.hasNegativeBalance && (
          <Text textStyle="captionStrong" color="Text.negativeHigh">
            {t(
              'page:procurementPlanDetail.submitConfirm.negativeBalanceWarning',
            )}
          </Text>
        )}
      </VStack>
    </Modal>
  )
}
